import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to check if we're in a Chrome extension context
const isChromeExtension = (): boolean => {
  return typeof chrome !== 'undefined' && !!chrome.runtime && !!chrome.runtime.id;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Function to check and validate session in multiple locations
    const checkAndValidateSession = async () => {
      try {
        // First try the official Supabase getSession
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setUser(session.user);
          return true;
        }
        
        // If no session found, check localStorage for tokens
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://djaohifjetmpwlszwirr.supabase.co';
        const storageKey = `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token`;
        
        // Check all possible storage keys
        const keys = [storageKey, 'supabase-auth-token', 'supabase-auth-token-v2', 'supabase.auth.token'];
        
        for (const key of keys) {
          try {
            const storedData = localStorage.getItem(key);
            if (storedData) {
              const sessionData = JSON.parse(storedData);
              if (sessionData && sessionData.access_token) {
                // Try to set the session with the found token
                const { data, error } = await supabase.auth.setSession({
                  access_token: sessionData.access_token,
                  refresh_token: sessionData.refresh_token || '',
                });
                
                if (!error && data.session) {
                  setUser(data.session.user);
                  return true;
                }
              }
            }
          } catch (e) {
            // Error checking storage ignored
          }
        }
        
        return false;
      } catch (error) {
        console.error('[AUTH] Error during session validation:', error);
        return false;
      } finally {
        setLoading(false);
      }
    };
    
    // Check active sessions and set the user
    checkAndValidateSession();
    
    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event: string, session: Session | null) => {
      console.log(`[AUTH] onAuthStateChange triggered. Event: ${_event}, Session: ${session ? 'Exists' : 'Null'}`);
      setUser(session?.user ?? null);
      setLoading(false);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  // Function to clear any stale authentication data
  const clearStaleAuth = () => {
    try {
      // Get the correct Supabase storage key for this project
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://djaohifjetmpwlszwirr.supabase.co';
      const storageKey = `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token`;
      
      // Clear all possible token storage locations
      const keysToRemove = [
        storageKey,
        'supabase-auth-token',
        'supabase-auth-token-v2',
        'supabase.auth.token',
        `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token-code-verifier` // Add PKCE verifier
      ];
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        document.cookie = `${key}=;path=/;max-age=0;SameSite=Lax`;
      });
      
      // Auth data cleared from storage
      console.log('[AUTH] Cleared stale auth data from storage.');
    } catch (e) {
      // Failed to clear storage, continue
      console.error('[AUTH] Failed to clear storage:', e);
    }
  };

  const signInWithGoogle = async () => {
    try {
      // Clear stale auth data first to ensure a clean login state
      clearStaleAuth();

      // Check if the current URL indicates it's an extension flow
      const isExtensionFlow = new URLSearchParams(window.location.search).get('extension') === 'true';
      console.log(`[AUTH] Initiating Google Sign-In. Extension flow: ${isExtensionFlow}`);

      // Get the current environment
      const hostname = window.location.hostname;

      // Handle different redirect URLs based on environment
      let baseRedirectUrl;

      // Special case for www.quickcoverletter.com domain
      if (hostname === 'www.quickcoverletter.com' || hostname === 'quickcoverletter.com') {
        // For production, use a fixed, simple URL to avoid stale session issues
        baseRedirectUrl = 'https://www.quickcoverletter.com/auth-callback';
      } else if (hostname === 'localhost') {
        // Local development
        baseRedirectUrl = `http://localhost:${window.location.port}/auth-callback`;
      } else {
        // Other environments (staging, etc.)
        baseRedirectUrl = `${window.location.protocol}//${hostname}/auth-callback`;
      }

      // Append extension=true parameter if it's an extension flow
      const finalRedirectUrl = isExtensionFlow ? `${baseRedirectUrl}?extension=true` : baseRedirectUrl;
      console.log(`[AUTH] Using redirect URL: ${finalRedirectUrl}`);

      // Initiate OAuth login with enhanced options
      const { error, data } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: finalRedirectUrl,
          queryParams: {
            // Add a timestamp to prevent caching issues
            t: Date.now().toString(),
            // Add access_type=offline to get refresh tokens
            access_type: 'offline',
            // Prompt user to select account to avoid automatic selection
            prompt: 'select_account'
          },
          skipBrowserRedirect: false
        }
      });

      if (error) {
        console.error('[AUTH] Sign in error:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('[AUTH] Unexpected error during sign in:', error);
      throw error;
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    console.log("[AUTH] User signed out");
  };

  const value = {
    user,
    loading,
    signInWithGoogle,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
